import { useCallback, useEffect, useState } from 'react';
import { useApi } from '../context';
import { DirectoryPicker } from '../components/DirectoryPicker';
import type { AccessAuditRow, AccessGroup, Role, Team } from '../types';

interface UserRow {
  id: string;
  upn: string;
  display_name: string;
  is_active: boolean;
  last_seen_at: string | null;
  roles: Role[];
  all_teams: boolean;
  team_ids: string[];
}
interface GroupHit { id: string; displayName: string; userPrincipalName: string }

const ALL_ROLES: Role[] = ['clinical_user', 'admin_user', 'on_call_user', 'manager', 'admin'];
const roleLabel = (r: string) => r.replace('_', ' ');

/** Admin only: manage user roles, group-based access, and review the access audit. */
export function UsersPage() {
  const api = useApi();
  const [users, setUsers] = useState<UserRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [teams, setTeams] = useState<Team[]>([]);
  const loadUsers = useCallback(() => api.get<UserRow[]>('/api/users').then(setUsers, (e: Error) => setError(e.message)), [api]);
  useEffect(() => { void loadUsers(); }, [loadUsers]);
  useEffect(() => { api.get<Team[]>('/api/teams').then(setTeams, () => undefined); }, [api]);

  async function toggleUser(u: UserRow, role: Role, grant: boolean) {
    const req = grant ? api.post(`/api/users/${u.id}/roles`, { role }) : api.delete(`/api/users/${u.id}/roles/${role}`);
    await req.catch((e: Error) => setError(e.message));
    void loadUsers();
  }

  async function toggleActive(u: UserRow) {
    await api.patch(`/api/users/${u.id}`, { isActive: !u.is_active }).catch((e: Error) => setError(e.message));
    void loadUsers();
  }

  async function addUser(entry: { id: string; displayName: string; userPrincipalName: string }) {
    await api.post('/api/users', { entraOid: entry.id, upn: entry.userPrincipalName, displayName: entry.displayName })
      .catch((e: Error) => setError(e.message));
    void loadUsers();
  }

  return (
    <>
      <h1>Users &amp; Access</h1>
      {error && <p className="error">{error}</p>}

      <h2>Users</h2>
      <p className="hint">Grant roles directly, or via a group below. Accounts are disabled until enabled — use <strong>enable</strong>, add someone from the directory before their first sign-in, or add a group (its members are enabled automatically at sign-in).</p>
      <table>
        <thead>
          <tr><th>Name</th><th>Account</th><th>Last seen</th>{ALL_ROLES.map((r) => <th key={r}>{roleLabel(r)}</th>)}<th>Teams</th></tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id}>
              <td>
                {u.display_name}{!u.is_active && <span className="badge missed"> not enabled</span>}{' '}
                <button className="link" onClick={() => toggleActive(u)}>{u.is_active ? 'disable' : 'enable'}</button>
              </td>
              <td>{u.upn}</td>
              <td>{u.last_seen_at ? new Date(u.last_seen_at).toLocaleString() : '—'}</td>
              {ALL_ROLES.map((r) => (
                <td key={r}><input type="checkbox" checked={u.roles.includes(r)} onChange={(e) => toggleUser(u, r, e.target.checked)} /></td>
              ))}
              <td><TeamsCell user={u} teams={teams} onSaved={loadUsers} onError={setError} /></td>
            </tr>
          ))}
        </tbody>
      </table>
      <DirectoryPicker
        label="🔍 Add a user (enable them before their first sign-in)"
        onPick={(e, kind) => { if (kind === 'user') void addUser(e); }}
      />

      <GroupsPanel onError={setError} />
      <AccessAuditPanel onError={setError} />
    </>
  );
}

function TeamsCell({ user, teams, onSaved, onError }: { user: UserRow; teams: Team[]; onSaved: () => void; onError: (m: string) => void }) {
  const api = useApi();
  const save = (allTeams: boolean, teamIds: string[]) =>
    api.put(`/api/users/${user.id}/teams`, { allTeams, teamIds }).then(onSaved, (e: Error) => onError(e.message));
  const toggle = (id: string, on: boolean) => save(false, on ? [...user.team_ids, id] : user.team_ids.filter((t) => t !== id));
  return (
    <div className="team-cell">
      <label><input type="checkbox" checked={user.all_teams} onChange={(e) => save(e.target.checked, e.target.checked ? [] : user.team_ids)} /> All</label>
      {!user.all_teams && teams.map((t) => (
        <label key={t.id} className="sub"><input type="checkbox" checked={user.team_ids.includes(t.id)} onChange={(e) => toggle(t.id, e.target.checked)} /> {t.name}</label>
      ))}
    </div>
  );
}

function GroupTeamsCell({ group, teams, onSaved, onError }: { group: AccessGroup; teams: Team[]; onSaved: () => void; onError: (m: string) => void }) {
  const api = useApi();
  const save = (allTeams: boolean, teamIds: string[]) =>
    api.put(`/api/groups/${group.id}/teams`, { allTeams, teamIds }).then(onSaved, (e: Error) => onError(e.message));
  const toggle = (id: string, on: boolean) => save(false, on ? [...group.team_ids, id] : group.team_ids.filter((t) => t !== id));
  return (
    <div className="team-cell">
      <label><input type="checkbox" checked={group.all_teams} onChange={(e) => save(e.target.checked, e.target.checked ? [] : group.team_ids)} /> All</label>
      {!group.all_teams && teams.map((t) => (
        <label key={t.id} className="sub"><input type="checkbox" checked={group.team_ids.includes(t.id)} onChange={(e) => toggle(t.id, e.target.checked)} /> {t.name}</label>
      ))}
    </div>
  );
}

function GroupsPanel({ onError }: { onError: (m: string) => void }) {
  const api = useApi();
  const [groups, setGroups] = useState<AccessGroup[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<GroupHit[] | null>(null);

  const load = useCallback(() => api.get<AccessGroup[]>('/api/groups').then(setGroups, (e: Error) => onError(e.message)), [api, onError]);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => { api.get<Team[]>('/api/teams').then(setTeams, () => undefined); }, [api]);

  async function toggleRole(g: AccessGroup, role: Role, grant: boolean) {
    const req = grant ? api.post(`/api/groups/${g.id}/roles`, { role }) : api.delete(`/api/groups/${g.id}/roles/${role}`);
    await req.catch((e: Error) => onError(e.message));
    void load();
  }
  async function search() {
    if (q.trim().length < 2) return;
    try { setHits(await api.get<GroupHit[]>(`/api/groups/search?q=${encodeURIComponent(q.trim())}`)); }
    catch (e) { onError((e as Error).message); }
  }
  async function add(hit: GroupHit) {
    await api.post('/api/groups', { entraOid: hit.id, displayName: hit.displayName }).catch((e: Error) => onError(e.message));
    setHits(null); setQ('');
    void load();
  }
  async function remove(g: AccessGroup) {
    if (!confirm(`Remove group "${g.display_name}"? Members lose any roles it grants (on their next sign-in).`)) return;
    await api.delete(`/api/groups/${g.id}`).catch((e: Error) => onError(e.message));
    void load();
  }

  return (
    <>
      <h2>Groups</h2>
      <p className="hint">Map roles to an Entra group; everyone in the group gains those roles at sign-in (in addition to any direct roles). Membership is read from Entra.</p>
      <table>
        <thead>
          <tr><th>Group</th>{ALL_ROLES.map((r) => <th key={r}>{roleLabel(r)}</th>)}<th>Teams</th><th></th></tr>
        </thead>
        <tbody>
          {groups.map((g) => (
            <tr key={g.id}>
              <td>{g.display_name}</td>
              {ALL_ROLES.map((r) => (
                <td key={r}><input type="checkbox" checked={g.roles.includes(r)} onChange={(e) => toggleRole(g, r, e.target.checked)} /></td>
              ))}
              <td><GroupTeamsCell group={g} teams={teams} onSaved={load} onError={onError} /></td>
              <td><button className="link danger" onClick={() => remove(g)}>remove</button></td>
            </tr>
          ))}
          {groups.length === 0 && <tr><td colSpan={ALL_ROLES.length + 3}>No groups yet</td></tr>}
        </tbody>
      </table>
      <div className="filters">
        <input placeholder="Search Entra groups (min 2)" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && search()} />
        <button onClick={search}>Search</button>
      </div>
      {hits && (
        <ul className="picker-results">
          {hits.map((h) => (
            <li key={h.id}><button className="link" onClick={() => add(h)}>{h.displayName}</button> <span className="sub">{h.userPrincipalName}</span></li>
          ))}
          {hits.length === 0 && <li><em>No groups matched</em></li>}
        </ul>
      )}
    </>
  );
}

function describe(row: AccessAuditRow): { verb: string; subject: string } {
  const role = row.detail?.role ? ` ${roleLabel(row.detail.role)}` : '';
  switch (row.action) {
    case 'user.role.grant':  return { verb: `granted${role}`, subject: row.user_name ?? 'a user' };
    case 'user.role.revoke': return { verb: `revoked${role}`, subject: row.user_name ?? 'a user' };
    case 'group.role.grant':  return { verb: `granted${role} to group`, subject: row.group_name ?? 'a group' };
    case 'group.role.revoke': return { verb: `revoked${role} from group`, subject: row.group_name ?? 'a group' };
    case 'group.add':    return { verb: 'added group', subject: row.group_name ?? (row.detail?.displayName ?? 'a group') };
    case 'group.remove': return { verb: 'removed group', subject: row.group_name ?? 'a group' };
    default: return { verb: row.action, subject: row.user_name ?? row.group_name ?? '' };
  }
}

function AccessAuditPanel({ onError }: { onError: (m: string) => void }) {
  const api = useApi();
  const [rows, setRows] = useState<AccessAuditRow[]>([]);
  const [limit, setLimit] = useState(25);

  useEffect(() => {
    api.get<AccessAuditRow[]>(`/api/access-audit?limit=${limit}`).then(setRows, (e: Error) => onError(e.message));
  }, [api, limit, onError]);

  return (
    <>
      <h2>Access audit</h2>
      <p className="hint">Who granted or revoked access, and when.</p>
      <table>
        <thead><tr><th>When</th><th>Who</th><th>Change</th><th>Subject</th></tr></thead>
        <tbody>
          {rows.map((r) => {
            const d = describe(r);
            return (
              <tr key={r.id}>
                <td>{new Date(r.at).toLocaleString()}</td>
                <td>{r.actor_name ?? '—'}</td>
                <td>{d.verb}</td>
                <td>{d.subject}</td>
              </tr>
            );
          })}
          {rows.length === 0 && <tr><td colSpan={4}>No access changes recorded yet</td></tr>}
        </tbody>
      </table>
      {rows.length >= limit && <button onClick={() => setLimit((l) => l + 25)}>Load more</button>}
    </>
  );
}
